import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import { supabase } from '../lib/supabase';
import { formatCurrency } from '../lib/payrollEngine';
import type { Employee } from '../lib/supabase';

const EMPTY_EMPLOYEE: Partial<Employee> = {
  first_name: '', last_name: '', dni: '', email: '', phone: '',
  position: '', department: '', hire_date: '', base_salary: 0,
  employment_type: 'full_time', has_family_assignment: false,
  family_members: 0, bank: '', bank_account: '', active: true,
  pension_system: undefined, pension_rate: undefined, health_system: undefined,
  shift_type: undefined, shift_start: '', shift_end: '',
  break_start: '', break_end: '', tolerance_minutes: undefined,
};

export function EmployeesPage() {
  const { company } = useAuth();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Partial<Employee>>(EMPTY_EMPLOYEE);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<'all' | 'active' | 'inactive'>('active');
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  useEffect(() => { if (company) loadEmployees(); }, [company]);

  function showToast(msg: string, type: 'success' | 'error' = 'success') {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3000);
  }

  async function loadEmployees() {
    setLoading(true);
    const { data } = await supabase
      .from('employees')
      .select('*')
      .eq('company_id', company!.id)
      .order('last_name');
    setEmployees(data || []);
    setLoading(false);
  }

  function openNew() { setEditing({ ...EMPTY_EMPLOYEE, company_id: company!.id }); setModalOpen(true); }
  function openEdit(emp: Employee) { setEditing({ ...emp }); setModalOpen(true); }

  async function handleSave() {
    if (!editing.first_name || !editing.last_name || !editing.dni || !editing.position || !editing.hire_date) {
      showToast('Completa los campos obligatorios', 'error'); return;
    }
    setSaving(true);
    if (editing.id) {
      const { error } = await supabase.from('employees').update(editing).eq('id', editing.id);
      if (error) { showToast('Error al actualizar', 'error'); } else { showToast('Empleado actualizado'); }
    } else {
      const { error } = await supabase.from('employees').insert({ ...editing, company_id: company!.id });
      if (error) { showToast(error.message.includes('unique') ? 'DNI ya registrado' : 'Error al crear', 'error'); }
      else { showToast('Empleado registrado'); }
    }
    setSaving(false);
    setModalOpen(false);
    loadEmployees();
  }

  async function handleToggleActive(emp: Employee) {
    await supabase.from('employees').update({ active: !emp.active }).eq('id', emp.id);
    showToast(emp.active ? 'Empleado desactivado' : 'Empleado activado');
    loadEmployees();
  }

  const filtered = employees.filter(e => {
    const matchSearch = `${e.first_name} ${e.last_name} ${e.dni} ${e.position} ${e.department}`.toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || (filter === 'active' ? e.active : !e.active);
    return matchSearch && matchFilter;
  });

  return (
    <div className="p-8 max-w-7xl mx-auto space-y-8">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-6 right-6 z-50 animate-fade-in flex items-center gap-3 px-5 py-3 rounded-xl shadow-xl text-white text-sm font-bold ${toast.type === 'success' ? 'bg-green-600' : 'bg-error'}`}>
          <span className="material-symbols-outlined text-[18px]">{toast.type === 'success' ? 'check_circle' : 'error'}</span>
          {toast.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex items-end justify-between">
        <div>
          <h1 className="text-4xl font-extrabold tracking-tight text-on-surface font-headline">Empleados</h1>
          <p className="text-on-surface-variant mt-1">Gestión del personal de tu empresa</p>
        </div>
        <button onClick={openNew} className="btn-primary text-white px-6 py-3 rounded-xl font-bold text-sm shadow-lg shadow-primary/20 flex items-center gap-2 hover:opacity-90 active:scale-[0.98] transition-all">
          <span className="material-symbols-outlined text-[20px]">person_add</span>
          Agregar empleado
        </button>
      </div>

      {/* Filters */}
      <div className="card p-4 flex flex-wrap gap-4 items-center">
        <div className="relative flex-1 min-w-48">
          <span className="material-symbols-outlined absolute left-3 top-1/2 -translate-y-1/2 text-outline text-[18px]">search</span>
          <input className="w-full bg-surface-container-low border-none rounded-lg pl-10 pr-4 py-2.5 text-sm focus:ring-2 focus:ring-primary/20 outline-none" placeholder="Buscar por nombre, DNI, cargo..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <div className="flex gap-1 p-1 bg-surface-container-low rounded-lg">
          {(['all', 'active', 'inactive'] as const).map(f => (
            <button key={f} onClick={() => setFilter(f)} className={`px-4 py-1.5 rounded-md text-xs font-bold transition-all ${filter === f ? 'bg-surface-container-lowest shadow text-primary' : 'text-on-surface-variant hover:text-primary'}`}>
              {f === 'all' ? 'Todos' : f === 'active' ? 'Activos' : 'Inactivos'}
            </button>
          ))}
        </div>
        <span className="text-xs text-on-surface-variant font-medium">{filtered.length} empleado(s)</span>
      </div>

      {/* Table */}
      <div className="card overflow-hidden">
        {loading ? (
          <div className="py-16 flex items-center justify-center">
            <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center">
            <span className="material-symbols-outlined text-4xl text-outline-variant">group</span>
            <p className="text-on-surface-variant text-sm mt-2">No hay empleados registrados</p>
            <button onClick={openNew} className="mt-4 text-primary text-sm font-bold hover:underline">+ Agregar el primero</button>
          </div>
        ) : (
          <table className="w-full text-left">
            <thead>
              <tr className="bg-surface-container-low">
                {['Empleado', 'Cargo / Área', 'DNI', 'Salario Base', 'Ingreso', 'Estado', ''].map(h => (
                  <th key={h} className="px-6 py-4 text-[10px] font-bold text-on-surface-variant uppercase tracking-widest">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-outline-variant/10">
              {filtered.map(emp => (
                <tr key={emp.id} className="hover:bg-surface-container-low/30 transition-colors group">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-secondary-container flex items-center justify-center font-bold text-primary text-sm">
                        {emp.first_name[0]}{emp.last_name[0]}
                      </div>
                      <div>
                        <p className="text-sm font-bold text-on-surface">{emp.first_name} {emp.last_name}</p>
                        <p className="text-xs text-on-surface-variant">{emp.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium">{emp.position}</p>
                    <p className="text-xs text-on-surface-variant uppercase tracking-tighter">{emp.department}</p>
                  </td>
                  <td className="px-6 py-4 text-sm font-mono text-on-surface">{emp.dni}</td>
                  <td className="px-6 py-4 text-sm font-semibold text-primary tabular-nums">{formatCurrency(emp.base_salary)}</td>
                  <td className="px-6 py-4 text-sm text-on-surface-variant">{emp.hire_date}</td>
                  <td className="px-6 py-4">
                    <span className={emp.active ? 'badge-neutral' : 'badge-error'}>{emp.active ? 'Activo' : 'Inactivo'}</span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button onClick={() => openEdit(emp)} className="p-2 text-on-surface-variant hover:text-primary hover:bg-surface-container-high rounded-lg transition-all">
                        <span className="material-symbols-outlined text-[18px]">edit</span>
                      </button>
                      <button onClick={() => handleToggleActive(emp)} className="p-2 text-on-surface-variant hover:text-amber-600 hover:bg-amber-50 rounded-lg transition-all">
                        <span className="material-symbols-outlined text-[18px]">{emp.active ? 'person_off' : 'person'}</span>
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal */}
      {modalOpen && (
        <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface-container-lowest rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto animate-fade-in">
            <div className="flex items-center justify-between p-6 border-b border-outline-variant/10">
              <h2 className="text-xl font-bold font-headline">{editing.id ? 'Editar empleado' : 'Nuevo empleado'}</h2>
              <button onClick={() => setModalOpen(false)} className="p-2 hover:bg-surface-container-high rounded-lg transition-colors">
                <span className="material-symbols-outlined">close</span>
              </button>
            </div>
            <div className="p-6 space-y-5">
              <div className="grid grid-cols-2 gap-4">
                {[
                  { label: 'Nombres *', field: 'first_name', type: 'text' },
                  { label: 'Apellidos *', field: 'last_name', type: 'text' },
                  { label: 'DNI *', field: 'dni', type: 'text' },
                  { label: 'Email', field: 'email', type: 'email' },
                  { label: 'Teléfono', field: 'phone', type: 'text' },
                  { label: 'Cargo *', field: 'position', type: 'text' },
                  { label: 'Área / Departamento', field: 'department', type: 'text' },
                  { label: 'Fecha de ingreso *', field: 'hire_date', type: 'date' },
                  { label: 'Salario base (S/.) *', field: 'base_salary', type: 'number' },
                  { label: 'Banco', field: 'bank', type: 'text' },
                  { label: 'N° Cuenta bancaria', field: 'bank_account', type: 'text' },
                ].map(({ label, field, type }) => (
                  <div key={field} className="space-y-1.5">
                    <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">{label}</label>
                    <input
                      className="input-base"
                      type={type}
                      value={(editing as any)[field] || ''}
                      onChange={e => setEditing(prev => ({ ...prev, [field]: type === 'number' ? parseFloat(e.target.value) || 0 : e.target.value }))}
                    />
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Tipo de contrato</label>
                  <select className="input-base" value={editing.employment_type || 'full_time'} onChange={e => setEditing(prev => ({ ...prev, employment_type: e.target.value as any }))}>
                    <option value="full_time">Tiempo completo</option>
                    <option value="part_time">Medio tiempo</option>
                    <option value="contract">Contrato</option>
                  </select>
                </div>
                <div className="flex items-center gap-3 mt-6">
                  <input type="checkbox" id="fam" checked={editing.has_family_assignment || false} onChange={e => setEditing(prev => ({ ...prev, has_family_assignment: e.target.checked }))} className="accent-primary w-4 h-4" />
                  <label htmlFor="fam" className="text-sm font-medium cursor-pointer">Asignación familiar (10% RMV)</label>
                </div>
              </div>

              {/* ── Configuración individual pensión y salud ── */}
              <div className="pt-2 border-t border-outline-variant/10">
                <p className="text-xs font-bold text-on-surface-variant uppercase tracking-wider mb-3">
                  Pensión y seguro de salud
                  <span className="ml-2 font-normal normal-case text-outline-variant">(si está vacío hereda configuración de la empresa)</span>
                </p>
                <div className="grid grid-cols-3 gap-4">
                  {/* Sistema pensionario */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Sistema pensionario</label>
                    <select
                      className="input-base"
                      value={editing.pension_system || ''}
                      onChange={e => setEditing(prev => ({
                        ...prev,
                        pension_system: e.target.value as any || undefined,
                        // sugerir tasa por defecto al cambiar sistema
                        pension_rate: e.target.value === 'onp' ? 13
                          : e.target.value === 'afp' ? (prev.pension_rate || 11.37)
                          : undefined,
                      }))}
                    >
                      <option value="">Hereda de empresa</option>
                      <option value="onp">ONP</option>
                      <option value="afp">AFP</option>
                      <option value="ninguno">Sin aporte</option>
                    </select>
                  </div>

                  {/* Tasa pensionaria */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">
                      Tasa pensión (%)
                    </label>
                    <input
                      className="input-base"
                      type="number"
                      step="0.01"
                      min="0"
                      max="20"
                      disabled={!editing.pension_system || editing.pension_system === 'ninguno'}
                      placeholder={editing.pension_system === 'onp' ? '13.00' : editing.pension_system === 'afp' ? 'Ej: 11.37' : '—'}
                      value={editing.pension_rate ?? ''}
                      onChange={e => setEditing(prev => ({ ...prev, pension_rate: parseFloat(e.target.value) || undefined }))}
                    />
                    {editing.pension_system === 'afp' && (
                      <p className="text-[10px] text-on-surface-variant">
                        Tasa fija legal: 10% aporte obligatorio + 1.37% prima seguro = 11.37%
                      </p>
                    )}
                  </div>

                  {/* Seguro de salud */}
                  <div className="space-y-1.5">
                    <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Seguro de salud</label>
                    <select
                      className="input-base"
                      value={editing.health_system || ''}
                      onChange={e => setEditing(prev => ({ ...prev, health_system: e.target.value as any || undefined }))}
                    >
                      <option value="">Hereda de empresa</option>
                      <option value="essalud">EsSalud (9% empleador)</option>
                      <option value="sis">SIS Microempresa (~S/ 15)</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* ── Horario individual ── */}
              <div className="pt-2 border-t border-outline-variant/10">
                <p className="text-xs font-bold text-on-surface-variant uppercase tracking-wider mb-3">
                  Horario de trabajo
                  <span className="ml-2 font-normal normal-case text-outline-variant">(si está vacío hereda horario general de la empresa)</span>
                </p>

                {/* Tipo de turno */}
                <div className="grid grid-cols-3 gap-3 mb-4">
                  {([
                    { value: 'full',      label: 'Turno completo', icon: 'schedule',     desc: 'Con refrigerio configurable' },
                    { value: 'morning',   label: 'Turno mañana',   icon: 'wb_sunny',     desc: 'Sin refrigerio' },
                    { value: 'afternoon', label: 'Turno tarde',    icon: 'nights_stay',  desc: 'Sin refrigerio' },
                  ] as {value: string, label: string, icon: string, desc: string}[]).map(t => (
                    <label
                      key={t.value}
                      className={`flex flex-col items-center gap-1 p-3 rounded-xl cursor-pointer border-2 text-center transition-all ${
                        editing.shift_type === t.value
                          ? 'border-primary bg-primary/5'
                          : 'border-outline-variant/20 hover:border-primary/30 hover:bg-surface-container-low'
                      }`}
                    >
                      <input
                        type="radio"
                        name="shift_type"
                        value={t.value}
                        checked={editing.shift_type === t.value}
                        onChange={e => setEditing(prev => ({
                          ...prev,
                          shift_type: e.target.value as any,
                          // limpiar refrigerio si no es turno completo
                          break_start: e.target.value !== 'full' ? '' : prev.break_start,
                          break_end:   e.target.value !== 'full' ? '' : prev.break_end,
                        }))}
                        className="sr-only"
                      />
                      <span className="material-symbols-outlined text-primary text-[20px]">{t.icon}</span>
                      <span className="text-xs font-bold text-on-surface">{t.label}</span>
                      <span className="text-[10px] text-on-surface-variant">{t.desc}</span>
                    </label>
                  ))}
                </div>

                {/* Solo se muestran campos si se eligió un turno */}
                {editing.shift_type && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-3 gap-4">
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Hora entrada</label>
                        <input
                          type="time"
                          className="input-base"
                          value={editing.shift_start || ''}
                          onChange={e => setEditing(prev => ({ ...prev, shift_start: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Hora salida</label>
                        <input
                          type="time"
                          className="input-base"
                          value={editing.shift_end || ''}
                          onChange={e => setEditing(prev => ({ ...prev, shift_end: e.target.value }))}
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-xs font-bold text-on-surface-variant uppercase tracking-wider">Tolerancia (min)</label>
                        <input
                          type="number"
                          className="input-base"
                          min="0"
                          max="60"
                          placeholder="Ej: 5"
                          value={editing.tolerance_minutes ?? ''}
                          onChange={e => setEditing(prev => ({ ...prev, tolerance_minutes: parseInt(e.target.value) || undefined }))}
                        />
                      </div>
                    </div>

                    {/* Refrigerio — solo para turno completo */}
                    {editing.shift_type === 'full' && (
                      <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl space-y-3">
                        <div className="flex items-center gap-2">
                          <span className="material-symbols-outlined text-amber-600 text-[18px]">restaurant</span>
                          <p className="text-xs font-bold text-amber-800">Refrigerio / Descanso</p>
                          <p className="text-[10px] text-amber-700 ml-1">(este tiempo NO cuenta como horas trabajadas)</p>
                        </div>
                        <div className="grid grid-cols-2 gap-4">
                          <div className="space-y-1.5">
                            <label className="text-xs font-bold text-amber-700 uppercase tracking-wider">Inicio refrigerio</label>
                            <input
                              type="time"
                              className="input-base bg-white"
                              value={editing.break_start || ''}
                              onChange={e => setEditing(prev => ({ ...prev, break_start: e.target.value }))}
                            />
                          </div>
                          <div className="space-y-1.5">
                            <label className="text-xs font-bold text-amber-700 uppercase tracking-wider">Fin refrigerio</label>
                            <input
                              type="time"
                              className="input-base bg-white"
                              value={editing.break_end || ''}
                              onChange={e => setEditing(prev => ({ ...prev, break_end: e.target.value }))}
                            />
                          </div>
                        </div>
                        {editing.break_start && editing.break_end && (
                          <p className="text-[10px] text-amber-700">
                            ⏱ Duración del refrigerio: {(() => {
                              const [sh, sm] = (editing.break_start || '00:00').split(':').map(Number);
                              const [eh, em] = (editing.break_end   || '00:00').split(':').map(Number);
                              const mins = (eh * 60 + em) - (sh * 60 + sm);
                              return mins > 0 ? `${mins} minutos` : '—';
                            })()}
                          </p>
                        )}
                      </div>
                    )}

                    {/* Preview del horario resultante */}
                    {editing.shift_start && editing.shift_end && (
                      <div className="p-3 bg-primary/5 rounded-xl border border-primary/15 text-xs">
                        <span className="font-bold text-primary">Horario:</span>
                        <span className="text-on-surface ml-2">
                          {editing.shift_start} → {editing.shift_end}
                          {editing.shift_type === 'full' && editing.break_start && editing.break_end
                            ? ` (refrigerio ${editing.break_start}–${editing.break_end})`
                            : ''}
                        </span>
                        <span className="text-on-surface-variant ml-2">
                          · {(() => {
                            const [sh, sm] = (editing.shift_start || '00:00').split(':').map(Number);
                            const [eh, em] = (editing.shift_end   || '00:00').split(':').map(Number);
                            let mins = (eh * 60 + em) - (sh * 60 + sm);
                            if (editing.shift_type === 'full' && editing.break_start && editing.break_end) {
                              const [bsh, bsm] = (editing.break_start).split(':').map(Number);
                              const [beh, bem] = (editing.break_end).split(':').map(Number);
                              mins -= (beh * 60 + bem) - (bsh * 60 + bsm);
                            }
                            return `${(mins / 60).toFixed(1)}h efectivas/día`;
                          })()}
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>
            <div className="flex gap-3 p-6 border-t border-outline-variant/10">
              <button onClick={() => setModalOpen(false)} className="flex-1 px-4 py-2.5 rounded-lg border border-outline-variant/30 text-sm font-bold text-on-surface-variant hover:bg-surface-container-low transition-all">Cancelar</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 btn-primary text-white py-2.5 rounded-lg font-bold text-sm shadow-md hover:opacity-90 transition-all disabled:opacity-60">
                {saving ? 'Guardando...' : (editing.id ? 'Actualizar' : 'Crear empleado')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
